import type { CampanaEnvioPublic, FiltrosAudiencia, PlantillaInput } from '@app/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Lock } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { Alert } from '../../../components/ui/alert';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { useI18n } from '../../../i18n/I18nProvider';
import { getErrorMessage } from '../../../lib/api';
import { qk } from '../../../lib/queryKeys';
import { emisionApi } from '../api/emision.api';
import { ContadorAudiencia, FiltrosAudienciaForm } from '../components/FiltrosAudiencia';
import { PlantillasForm, ProbarEnvio, RitmoForm, type Ritmo } from '../components/MensajesYRitmo';

// ciclo-emisor, parte 2: armar (o editar) una campaña. Cuatro pasos en una sola
// página: a quién, mensajes, ritmo, probar. Se guarda DESACTIVADA: activarla es la
// casilla de la lista. Con la lista ya fija, los filtros se ven pero no se cambian.

const FILTROS_INICIALES: FiltrosAudiencia = { origenes: ['APP', 'NOTION'] };
const RITMO_INICIAL: Ritmo = { topeDiario: 20, intervaloMin: 30, horaInicio: 10, horaFin: 20 };

export function CampanaEditorPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const es = useI18n();
  const t = es.campanasEnvio;
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existente = useQuery({
    queryKey: qk.campanasEnvio.detail(id ?? ''),
    queryFn: () => emisionApi.get(id!),
    enabled: Boolean(id),
  });
  const opciones = useQuery({ queryKey: qk.campanasEnvio.opciones, queryFn: emisionApi.opciones });

  const [nombre, setNombre] = useState('');
  const [filtros, setFiltros] = useState<FiltrosAudiencia>(FILTROS_INICIALES);
  const [plantillas, setPlantillas] = useState<PlantillaInput[]>([
    { nombre: t.plantillas.nueva.replace('{n}', '1'), texto: 'Hola {{nombre}}, ', activa: true },
  ]);
  const [ritmo, setRitmo] = useState<Ritmo>(RITMO_INICIAL);
  const [hasta, setHasta] = useState('');

  // Al editar, el formulario arranca con lo guardado.
  useEffect(() => {
    const c: CampanaEnvioPublic | undefined = existente.data;
    if (!c) return;
    setNombre(c.nombre);
    setFiltros(c.filtros);
    setPlantillas(c.plantillas);
    setRitmo({
      topeDiario: c.topeDiario,
      intervaloMin: c.intervaloMin,
      horaInicio: c.horaInicio,
      horaFin: c.horaFin,
    });
    setHasta(c.vigenteHasta?.slice(0, 10) ?? '');
  }, [existente.data]);

  const bloqueado = Boolean(existente.data?.listaFijadaEn);
  const vigenteHasta = hasta ? new Date(`${hasta}T23:59:59-03:00`) : null;

  const guardar = useMutation({
    mutationFn: () =>
      id
        ? emisionApi.actualizar(id, {
            nombre,
            ...(bloqueado ? {} : { filtros }),
            plantillas,
            ...ritmo,
            vigenteHasta,
          })
        : emisionApi.crear({ nombre, filtros, plantillas, ...ritmo, vigenteHasta }),
    onSuccess: (c) => {
      toast.success(t.editor.guardado);
      void queryClient.invalidateQueries({ queryKey: qk.campanasEnvio.all });
      navigate(`/campanas/${c.id}`);
    },
    onError: (e) => toast.error(getErrorMessage(e, es.errores.generico.algoSalioMal)),
  });

  if (id && existente.isLoading) return <p className="p-6 text-sm text-text-subtle">{t.cargando}</p>;

  return (
    <div className="mx-auto max-w-6xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild>
            <Link to={id ? `/campanas/${id}` : '/campanas'}>
              <ArrowLeft size={16} />
              {t.editor.volver}
            </Link>
          </Button>
          <h1 className="font-titulos text-3xl font-bold text-text-marca">
            {id ? t.editor.tituloEditar : t.editor.tituloNueva}
          </h1>
        </div>
        <Button onClick={() => guardar.mutate()} disabled={guardar.isPending || nombre.trim().length < 2}>
          {t.editor.guardar}
        </Button>
      </div>
      {existente.error && (
        <Alert variant="error">{getErrorMessage(existente.error, es.errores.generico.algoSalioMal)}</Alert>
      )}

      <label className="flex items-center gap-3 text-sm font-medium text-text-strong">
        {t.editor.nombre}
        <Input
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
          placeholder={t.editor.nombrePlaceholder}
          className="max-w-md"
        />
      </label>

      <Seccion titulo={t.editor.pasoQuien}>
        {bloqueado && (
          <p className="mb-4 flex items-center gap-2 text-sm text-text-subtle">
            <Lock size={14} />
            {t.editor.listaFija}
          </p>
        )}
        <div className="grid grid-cols-[1fr_340px] items-start gap-6">
          <FiltrosAudienciaForm
            filtros={filtros}
            onChange={setFiltros}
            opciones={opciones.data}
            bloqueado={bloqueado}
          />
          {!bloqueado && (
            <div className="sticky top-20">
              <ContadorAudiencia filtros={filtros} />
            </div>
          )}
        </div>
      </Seccion>

      <Seccion titulo={t.editor.pasoMensajes}>
        <PlantillasForm plantillas={plantillas} onChange={setPlantillas} />
      </Seccion>

      <Seccion titulo={t.editor.pasoRitmo}>
        <RitmoForm ritmo={ritmo} onChange={setRitmo} />
        <label className="mt-3 flex items-center gap-2 text-sm text-text-subtle">
          {t.ritmo.hasta}
          <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)} className="h-9 w-44" />
        </label>
      </Seccion>

      <Seccion titulo={t.editor.pasoProbar}>
        <ProbarEnvio plantillas={plantillas} numeroInicial={opciones.data?.numeroPrueba ?? null} />
      </Seccion>
    </div>
  );
}

function Seccion({ titulo, children }: { titulo: string; children: React.ReactNode }): JSX.Element {
  return (
    <section className="rounded-xl border border-border-subtle bg-surface p-5 shadow-card">
      <h2 className="mb-4 font-titulos text-xl font-bold text-text-strong">{titulo}</h2>
      {children}
    </section>
  );
}
